import BaseEntity from "@/core/misc/BaseEntity";
import TYPES                    from "@/core/services/Changelogs/types";
import Utils                    from "@/core/utils/Utils";

import EntityPointer from "@/domains/EntityPointer/EntityPointer";
import TimeStamp from "@/core/types/TimeStamp";

import Title_ru_RU from "@/core/misc/decorators/Title_ru_RU";
import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";
/**
 * Дескриптор файла - модель для представления файла в системе
 * Содержит информацию о файле и методы для работы с ним
 */
@Title_ru_RU_Class('Файл')                          // FileDescriptor → FileDescriptor  ⚠ ключ с большой 'F', в Dictionary 'file'
class FileDescriptor extends BaseEntity {
  uid: string;

  @Title_ru_RU('Название файла', TYPES.STRING)
  name: string;
  @Title_ru_RU('Размер', TYPES.STRING)
  size: number;

  mime: string;
  parentEntity: EntityPointer;
  isAlreadyDownloaded: boolean;

  // Дополнительные поля для chunked загрузки
  isChunked: boolean;
  chunkNumber?: number;
  totalChunks?: number;


  static fromServer(fileData: any, file = new FileDescriptor()) {
    super.fromServer(fileData, file);

    file.id = file.uid;

    file.createdAt = TimeStamp.fromServer(fileData.createdAt);

    return file;
  }

  /**
   * Размер файла в удобочитаемом формате
   */
  get formattedSize(): string {
    return Utils.file.formatSize(this.size || 0);
  }

  get extension(): string {
    if (!this.name || !this.name.includes('.')) {
      return '';
    }
    return this.name.split('.').pop()!.toLowerCase();
  }

  get nameWithoutExtension(): string {
    if (!this.name || !this.name.includes('.')) {
      return this.name || '';
    }
    const parts = this.name.split('.');
    parts.pop();
    return parts.join('.');
  }

  get fileIcon(): string {
		return Utils.file.ICONS_MAP[this.extension] || Utils.file.ICONS_MAP['any'];
  }

  get fileCategory(): string {
    const ext = this.extension;

    // Изображения
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) {
      return 'image';
    }

    // Документы
    if (['pdf', 'doc', 'docx', 'odt', 'rtf', 'txt'].includes(ext)) {
      return 'document';
    }

    // Таблицы
    if (['xls', 'xlsx', 'calc', 'ods', 'csv'].includes(ext)) {
      return 'spreadsheet';
    }

    // Архивы
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
      return 'archive';
    }

    // Видео
    if (['mp4', 'avi', 'mkv', 'mov', 'wmv', 'webm'].includes(ext)) {
      return 'video';
    }

    // Аудио
    if (['mp3', 'wav', 'flac', 'ogg', 'aac'].includes(ext)) {
      return 'audio';
    }

    return 'other';
  }

  get isImage(): boolean {
    return this.fileCategory === 'image';
  }

  get isDocument(): boolean {
    return this.fileCategory === 'document';
  }

  /**
   * Проверка возможности предпросмотра с учетом типа файла и размера
   */
  get isPreviewable(): boolean {
    // Проверяем тип файла
    const previewableExtensions = ['pdf', 'txt', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
    const canPreviewType = previewableExtensions.includes(this.extension);

    if (!canPreviewType) return false;

    // Проверяем размер (10MB лимит)
    const maxPreviewSize = 10 * 1024 * 1024;
    return (this.size || 0) <= maxPreviewSize;
  }

  /**
   * Причина недоступности предпросмотра для UI
   */
  get previewDisabledReason(): string | null {
    const previewableExtensions = ['pdf', 'txt', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];

    if (!previewableExtensions.includes(this.extension)) {
      return `Предпросмотр недоступен для файлов типа ${this.extension.toUpperCase()}`;
    }

    const maxPreviewSize = 10 * 1024 * 1024;
    if ((this.size || 0) > maxPreviewSize) {
      const maxSizeFormatted = Utils.file.formatSize(maxPreviewSize);
      return `Файл слишком большой для предпросмотра (максимум ${maxSizeFormatted})`;
    }

    return null;
  }

  get mimeType(): string {
    return this.mime || Utils.file.getMimeType(this.name);
  }

  get chunkInfo(): string | null {
    if (!this.isChunked || !this.chunkNumber || !this.totalChunks) {
      return null;
    }

    return `Часть ${this.chunkNumber} из ${this.totalChunks}`;
  }

  get uploadProgress(): number | null {
    if (!this.isChunked || !this.chunkNumber || !this.totalChunks) {
      return null;
    }

    return Math.round((this.chunkNumber / this.totalChunks) * 100);
  }

  markAsDownloaded(): void {
    this.isAlreadyDownloaded = true;
  }

  resetDownloadStatus(): void {
    this.isAlreadyDownloaded = false;
  }

  validate(): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!this.name || this.name.trim() === '') {
      errors.push('Название файла не может быть пустым');
    }

		if (!this.uid || this.uid.trim() === '') {
      errors.push('UID файла обязателен');
    }

    if (this.size === null || this.size === undefined || this.size < 0) {
      errors.push('Размер файла должен быть неотрицательным числом');
    }

    if (this.isChunked) {
      if (!this.chunkNumber || this.chunkNumber < 1) {
        errors.push('Номер чанка должен быть положительным числом');
      }

      if (!this.totalChunks || this.totalChunks < 1) {
        errors.push('Общее количество чанков должно быть положительным числом');
      }

      if (this.chunkNumber && this.totalChunks && this.chunkNumber > this.totalChunks) {
        errors.push('Номер чанка не может превышать общее количество чанков');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export default FileDescriptor;
